import type { ReaperBatchLimits } from './internal-reaper-limits.ts';

/**
 * Retention for `request_lifecycle_metrics` (#2375 / #2354): the gateway
 * route `POST /internal/lifecycle-metrics/cleanup` and the control-loops
 * handler that drives it share this module, so the batch shape is one
 * number on both ends.
 *
 * The table is one row per request, keyed by `request_id`. It is
 * gateway-owned and region-local (excluded from replication), so jp and cn
 * each reap their own copy. Nothing in the billing chain reads it:
 * `usage_events → usage_rated_events → revenue_ledger` never touches it, and
 * deleting a row here can never change what a customer was charged.
 *
 * Age alone is the DELETE predicate: nothing references this table since
 * the retired per-event table and its FK were dropped (gateway 0094, #2653).
 *
 * Sampled rows (#2937, `LIFECYCLE_METRICS_SAMPLED=1`): only a kept request
 * has a row, carrying `sample_weight` (NULL = 1). Readers that count rows sum
 * `COALESCE(sample_weight, 1)`; readers that LEFT JOIN by request id see the
 * same NULLs for an unsampled request as for one past the TTL. Per reader:
 * docs/runbooks/request-lifecycle-writes.md § Sampled metrics rows.
 *
 * Readers that LEFT JOIN this table keep their SQL. After the TTL they
 * simply see NULL for rlm-sourced columns:
 *   - tenant `/internal/usage/requests` (`client_response_elapsed_ms`, …)
 *   - ops `/internal/usage/canonical-model-usage` (window clamped to Tokyo
 *     retention — a longer `days` cannot observe rows that no longer exist)
 *   - ops traffic overview: `MAX_CUSTOM_WINDOW_MS` only caps the span
 *     (31 days), not how old `from` may be. `resolveTrafficRange` refuses a
 *     primary `from` older than `LIFECYCLE_METRICS_RETENTION_DAYS`. Cache
 *     windows derived from `to` (1h / 24h / 7d) are truncated to the cutoff
 *     in `queryCacheWindows`, not refused — the returned window object
 *     carries the truncated `from`.
 */

/**
 * Tokyo (and every non-`cn` region) window. Measured on
 * `request_started_at`. Default for the `LIFECYCLE_METRICS_RETENTION_DAYS`
 * process-env knob (#2560); an invalid value fails the process at boot.
 * 10 days, aligned with the events / event-batches window
 * (`LIFECYCLE_EVENTS_RETENTION_DAYS`): the per-request summary has no reader
 * that needs history the timeline no longer has (traffic overview presets
 * top out at 7d, patrol at 3d, request detail LEFT JOINs by id). Was 45
 * (3.2 GB on Tokyo, 2026-09-21); money and usage live in `usage_logs` /
 * the ledgers, not here.
 */
export const LIFECYCLE_METRICS_RETENTION_DAYS = 10;

/**
 * CN window (`GATEWAY_REGION === 'cn'`). Same as Tokyo since both dropped
 * to the events window (was 30 vs 45). Default for
 * `LIFECYCLE_METRICS_RETENTION_DAYS_CN`; must stay ≤ the Tokyo window.
 */
export const LIFECYCLE_METRICS_RETENTION_DAYS_CN = 10;

/**
 * Rows removed per DELETE statement. Small on purpose: each batch is one
 * DELETE in its own short transaction that walks
 * `idx_request_lifecycle_metrics_started` from the oldest end
 * (`ORDER BY request_started_at ASC`), so no single statement holds row
 * locks or dirties pages for long on the database that also serves auth.
 *
 * Default for the gateway knob of the same name (#2681), as are the fuse
 * and budget below; ceilings and their derivation: `reaper-limits.mjs`.
 */
export const LIFECYCLE_METRICS_CLEANUP_BATCH_ROWS = 5_000;

/**
 * Fuse on DELETE statements per route call — not a traffic quota. 8 × 5 000
 * is 40k/tick × 144 = 5.76 M/day only if every batch fills AND finishes
 * inside the 1.5s budget. The budget is what actually stops the pass; a
 * short batch means the window is met. If the budget trips, overdue rows
 * wait for the next tick (true drain, not a percentage).
 * Do not raise this to 40: more batches would only matter if they still fit
 * in 1.5s, and a slow tick must abort rather than keep deleting on the
 * database that serves auth.
 */
export const LIFECYCLE_METRICS_CLEANUP_MAX_BATCHES = 8;

/**
 * Wall-clock budget for one route call, checked BEFORE each batch. This is
 * the stutter fuse: a slow tick aborts rather than keep deleting. jp + cn
 * serial + HTTP must stay far under the control-loops 25 s kill. Worst case
 * is one batch admitted at the last moment and running to statement_timeout
 * (~1.5 s + 5 s per region).
 */
export const LIFECYCLE_METRICS_CLEANUP_TIME_BUDGET_MS = 1_500;

/**
 * Per-statement bound (`SET LOCAL statement_timeout`) on every DELETE and on
 * the oldest-row read, so one stalled statement cannot outlive the pass.
 */
export const LIFECYCLE_METRICS_CLEANUP_STATEMENT_TIMEOUT_MS = 5_000;

export interface LifecycleMetricsCleanupResponse {
  readonly deletedRows: number;
  /** DELETE statements issued this call (0 when the budget or fuse admitted none). */
  readonly batches: number;
  /**
   * Age in days of the oldest surviving row. `null` when the table is empty,
   * or when the pass already exhausted the wall-clock budget (the age query
   * is skipped so jp + HTTP stay inside the control-loops fetch timeout).
   * Above the region window while draining; the patrol reads it to tell
   * "still catching up" from "the reaper stopped".
   */
  readonly oldestRemainingAgeDays: number | null;
  /**
   * Window this call actually used: `LIFECYCLE_METRICS_RETENTION_DAYS` on
   * Tokyo, `LIFECYCLE_METRICS_RETENTION_DAYS_CN` when `GATEWAY_REGION` is
   * `cn`. Echoed so a log line does not have to remember which region it hit.
   */
  readonly retentionDays: number;
  readonly errors: readonly string[];
  /**
   * Limits this call actually used (#2681): the contract defaults unless an
   * operator knob is set and valid. Additive; absent from older gateways.
   */
  readonly limits?: ReaperBatchLimits;
  /** Knobs this call IGNORED (invalid or above ceiling). Absent when none. */
  readonly configWarnings?: readonly string[];
}
